import { Injectable } from "@angular/core";
import { DateFormatEnum } from "@models/core/date-format";
import { IShift } from "@dmms/shared/data-access/resource-management/models/shift";
import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addSeconds,
  addYears,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  differenceInYears,
  format,
  formatDate,
  startOfDay,
} from "date-fns";

@Injectable({
  providedIn: "root",
})
export class DateUtilsService {
  static shifts: IShift[] = [];

  public static initShifts(shifts: IShift[]): void {
    DateUtilsService.shifts = shifts.map((shift) => {
      return {
        ...shift,
        ...{
          startTimeSeconds: DateUtilsService.getShiftTimeSeconds(
            shift,
            "startTime",
          ),
          endTimeSeconds: DateUtilsService.getShiftTimeSeconds(
            shift,
            "endTime",
          ),
        },
      };
    });
  }

  public static getFullDate(date: string): string {
    return `${formatDate(date, DateFormatEnum.YearMonthDay)}T${formatDate(
      date,
      DateFormatEnum.Time,
    )}${formatDate(date, DateFormatEnum.TimeOffset)}`;
  }

  public static getFullLocaleDate(date: string): string {
    return `${formatDate(date, DateFormatEnum.YearMonthDay)}T${formatDate(
      date,
      DateFormatEnum.Time,
    )}Z`;
  }

  public static getPeriod(seconds: number, withNegative: boolean = false) {
    if (!withNegative) {
      seconds = Math.max(0, seconds);
    }
    if (isNaN(seconds)) {
      seconds = 0;
    }
    const negative = seconds < 0;
    seconds = Math.abs(seconds);
    const secondInDay = 24 * 60 * 60;
    const secondInHour = 60 * 60;
    const secondInMinute = 60;
    const days = Math.floor(seconds / secondInDay);
    const diffDay = seconds - secondInDay * days;
    const hours = Math.floor(diffDay / secondInHour);
    const diffHour = diffDay - secondInHour * hours;
    const minutes = Math.floor(diffHour / secondInMinute);
    const secs = Math.round(diffHour - secondInMinute * minutes);

    if (days === 0) {
      if (hours == 0) {
        if (minutes == 0) {
          return `${negative ? "-" : ""}${secs}сек.`;
        }
        return `${negative ? "-" : ""}${minutes}мин. ${secs}сек.`;
      }
      return `${negative ? "-" : ""}${hours}ч. ${minutes}мин.`;
    }
    return `${negative ? "-" : ""}${days}дн. ${hours}ч.`;
  }

  public static getFullPeriod(date: string): string {
    const isDate =
      format(date, DateFormatEnum.YearMonthDay) == "0000-12-31" + "";

    if (isDate) {
      return "-";
    }

    const now = new Date();
    const diffDate = new Date(date);

    const diffYear = differenceInYears(now, diffDate);
    const month = addYears(diffDate, diffYear);
    const diffMonth = differenceInMonths(now, month);

    if (diffYear) {
      return `${diffYear}${diffYear > 4 ? "л" : "г"} ${diffMonth}м`;
    }

    const day = addMonths(month, diffMonth);
    const diffDay = differenceInDays(now, day);

    if (diffMonth) {
      return `${diffMonth}м ${diffDay}д`;
    }

    const hour = addDays(day, diffDay);
    const diffHour = differenceInHours(now, hour);

    if (diffDay) {
      return `${diffDay}д ${diffHour}ч`;
    }

    const minute = addHours(hour, diffHour);
    const diffMinute = differenceInMinutes(now, minute);

    if (diffHour) {
      return `${diffHour}ч ${diffMinute}м`;
    }

    const second = addMinutes(minute, diffMinute);
    const diffSecond = differenceInSeconds(now, second);

    if (diffMinute) {
      return `${diffMinute}м ${diffSecond}с`;
    }

    return "";
  }

  public static getShortPeriod(
    seconds: number,
    withNegative: boolean = false,
    onlyHours?: boolean,
  ) {
    if (!withNegative) {
      seconds = Math.max(0, seconds);
    }
    if (isNaN(seconds)) {
      seconds = 0;
    }
    const negative = seconds < 0;
    seconds = Math.abs(seconds);
    const secondInDay = 24 * 60 * 60;
    const secondInHour = 60 * 60;
    const secondInMinute = 60;
    const days = Math.floor(seconds / secondInDay);
    const diffDay = seconds - secondInDay * days;
    const hours = Math.floor(diffDay / secondInHour);
    const diffHour = diffDay - secondInHour * hours;
    const minutes = Math.floor(diffHour / secondInMinute);
    const secs = Math.round(diffHour - secondInMinute * minutes);

    if (days === 0) {
      if (hours == 0) {
        if (minutes == 0) {
          return `${negative ? "-" : ""}${secs}сек.`;
        }
        return `${negative ? "-" : ""}${minutes}м ${secs}с`;
      }
      return `${negative ? "-" : ""}${hours}ч ${minutes}м`;
    }
    if (onlyHours) {
      return `${negative ? "-" : ""}${hours + days * 24}ч ${minutes}м`;
    }
    return `${negative ? "-" : ""}${days}дн ${hours}ч`;
  }

  public static getPeriodFromNow(startDate: Date) {
    const endDate = new Date();
    return this.getPeriodBetween(startDate, endDate);
  }

  public static getPeriodBetween(startDate: Date, endDate: Date) {
    const seconds = Math.round(
      (endDate.getTime() - startDate.getTime()) / 1000,
    );
    return this.getPeriod(Math.abs(seconds));
  }

  public static getShortPeriodBetween(startDate: Date, endDate: Date) {
    const seconds = Math.round(
      (endDate.getTime() - startDate.getTime()) / 1000,
    );
    return this.getShortPeriod(Math.abs(seconds));
  }

  public static getShortPeriodFromNow(startDate: Date) {
    const endDate = new Date();
    return this.getShortPeriodBetween(startDate, endDate);
  }

  public static getDuration(startDate: string, endDate: string) {
    return (new Date(endDate).getTime() - new Date(startDate).getTime()) / 1000;
  }

  public static getShiftTimeSeconds(
    shift: IShift,
    field: "startTime" | "endTime",
  ): number | undefined {
    const fieldValue = shift[field];
    const arrays = fieldValue.split(":");

    if (arrays?.length < 2) {
      return undefined;
    }

    const regex = /-/;
    const isNegative = !!arrays[0].match(regex);

    if (isNegative) {
      arrays[0] = arrays[0].replace("-", "");
    }

    const hours = parseInt(arrays[0], 10);
    const minutes = parseInt(arrays[1], 10);
    const seconds = parseInt(arrays[2], 10);
    const totalSeconds = hours * 3600 + minutes * 60 + seconds;

    if (isNegative) {
      return Number("-" + totalSeconds);
    }

    return totalSeconds;
  }

  public static getShiftStartDate(date: Date, shift: IShift): Date {
    if (!shift) {
      return new Date(date);
    }

    if (!shift?.startTimeSeconds) {
      const startTimeSeconds = DateUtilsService.getShiftTimeSeconds(
        shift,
        "startTime",
      );

      if (!startTimeSeconds) {
        return new Date(date);
      }

      shift.startTimeSeconds = startTimeSeconds;
    }

    let calculatedDate = addSeconds(startOfDay(date), shift.startTimeSeconds);

    while (calculatedDate < date) {
      calculatedDate = addDays(calculatedDate, 1);
    }

    return calculatedDate;
  }

  public static getShiftEndDate(date: Date, shift: IShift): Date {
    if (!shift) return new Date(date);

    if (!shift?.endTimeSeconds) {
      shift.endTimeSeconds =
        DateUtilsService.getShiftTimeSeconds(shift, "endTime") ?? 0;
    }

    let calculatedDate = addSeconds(startOfDay(date), shift.endTimeSeconds);

    if (calculatedDate < date) {
      calculatedDate = addDays(calculatedDate, 1);
    }

    return calculatedDate;
  }

  public static getCurrentShift(date: Date = new Date()): IShift | undefined {
    const time = date.getTime();
    const shifts = DateUtilsService.shifts || [];

    if (!shifts?.length) {
      return undefined;
    }

    let activeShift: IShift | undefined = undefined;

    let day = addDays(startOfDay(date), -1);
    const toDay = addDays(startOfDay(date), 1);

    while (day.getTime() <= toDay.getTime()) {
      shifts.forEach((shift) => {
        const startTime = DateUtilsService.getShiftStartDate(
          day,
          shift,
        ).getTime();

        const endTime = DateUtilsService.getShiftEndDate(day, shift).getTime();

        if (time >= startTime && time <= endTime) {
          activeShift = shift;
        }
      });

      day = addDays(startOfDay(day), 1);
    }

    if (!activeShift) {
      activeShift = shifts[0];
    }

    return activeShift;
  }

  public static convertDateToUTC(date: Date | string): string | undefined {
    let parsedDate: Date;

    // Check if the input is a string and try to parse it
    if (typeof date === "string") {
      parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        return undefined;
      }
    } else if (date instanceof Date) {
      parsedDate = date;
    } else {
      return undefined;
    }

    // Use toISOString to convert to UTC
    return parsedDate.toISOString();
  }

  public static generateHourlyIntervals(
    shift?: IShift,
    dateTo?: Date,
  ): string[] {
    const times: string[] = [];

    let startHours = 0;
    let endHours = 23;
    let endMinutes = 59;

    if (shift) {
      [startHours] = shift.startTime
        ? shift.startTime.split(":").map(Number)
        : [0];
      [endHours, endMinutes] = shift.endTime
        ? shift.endTime.split(":").map(Number)
        : [23, 59];
    } else if (dateTo) {
      startHours = 0;
      endHours = 23;
      endMinutes = 59;
    }

    while (true) {
      times.push(`${String(startHours).padStart(2, "0")}:00`);

      if (startHours === endHours) {
        if (endMinutes > 0) {
          times.push(`${String((endHours + 1) % 24).padStart(2, "0")}:00`);
        }
        break;
      }
      startHours = (startHours + 1) % 24;
    }

    const ranges: string[] = [];
    for (let i = 0; i < times.length - 1; i++) {
      ranges.push(`${times[i]}-${times[i + 1]}`);
    }

    return ranges;
  }

  /* Перевод дней в часы и прибавление к остальным часам, чтобы получить формат HH:mm:ss
    Пример: input = "2.23:43:00"; result = "71:43:00";
    input = "23:44:00"; result = "23:44:00";
  */
  public static convertToTotalTime(input: string): string {
    if (!input) {
      return "";
    }
    let days = 0;
    let timePart = input;

    if (input.includes(".")) {
      const [daysPart, time] = input.split(".");
      days = parseInt(daysPart, 10);
      timePart = time;
    }

    const [hoursStr, minutesStr, secondsStr] = timePart.split(":");
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
    const seconds = parseInt(secondsStr, 10);

    const totalHours = days * 24 + hours;

    const pad = (n: number) => n.toString().padStart(2, "0");

    return `${totalHours}:${pad(minutes)}:${pad(seconds)}`;
  }

  /* Функция applyLocalTimeShift преобразует все указанные в объекте shift времена из UTC в локальное время пользователя, с учётом системного часового пояса. Пример: startTime: "03:00:00" станет "08:00:00" */
  public static applyLocalTimeShift(shift: IShift): IShift | undefined {
    if (!shift || Object.keys(shift).length === 0) {
      return undefined;
    }

    const baseDate = new Date(
      format(new Date(), DateFormatEnum.YearMonthDayAndFullTime) + " Z",
    );

    const date = `${format(
      startOfDay(new Date(baseDate)),
      DateFormatEnum.YearMonthDayAndFullTime,
    )} Z`;
    const start = addSeconds(
      new Date(date),
      DateUtilsService.getShiftTimeSeconds(shift, "startTime") ?? 0,
    );
    const end = addSeconds(
      new Date(date),
      DateUtilsService.getShiftTimeSeconds(shift, "endTime") ?? 0,
    );

    const getSecondsFromMidnight = (date: Date): number =>
      date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();

    return {
      ...shift,
      startTime: format(new Date(start), DateFormatEnum.FullTime),
      endTime: format(new Date(end), DateFormatEnum.FullTime),
      workStartTime: format(new Date(start), DateFormatEnum.FullTime),
      workEndTime: format(new Date(end), DateFormatEnum.FullTime),
      startTimeSeconds: getSecondsFromMidnight(start),
      endTimeSeconds: getSecondsFromMidnight(end),
    };
  }

  /**
   * Парсит строку формата "dd.MM.yyyy HH:mm:ss" в объект Date.
   * Возвращает null, если строка не соответствует формату или дата некорректна.
   */
  public static parseDateTime(dateTimeStr: string): Date | null {
    const [datePart, timePart] = dateTimeStr.split(" ");

    if (!datePart || !timePart) return null;

    const [day, month, year] = datePart.split(".").map(Number);
    const [hours, minutes, seconds] = timePart.split(":").map(Number);

    if (
      !day ||
      !month ||
      !year ||
      hours === undefined ||
      minutes === undefined ||
      seconds === undefined
    ) {
      return null;
    }

    const date = new Date(year, month - 1, day, hours, minutes, seconds);

    return Number.isFinite(date.getTime()) ? date : null;
  }

  /**
   * Конвертирует время в формате "HH:mm", заданное в UTC,
   * в локальное время пользователя.
   * @param utcTime Время в UTC в формате "HH:mm"
   * @returns Локальное время в формате "HH:mm"
   */
  public static convertTimeToLocal(utcTime: string): string {
    const [hours, minutes] = utcTime.split(":").map(Number);
    const date = new Date();
    date.setUTCHours(hours, minutes, 0, 0);
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  }

  /**
   * Конвертирует локальное время пользователя в формате "HH:mm"
   * во время UTC.
   * @param localTime Локальное время в формате "HH:mm"
   * @returns Время в UTC в формате "HH:mm"
   */
  public static convertTimeToUTC(localTime: string): string {
    const [hours, minutes] = localTime.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return `${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}`;
  }
}
